import { useState } from "react";
import { Check, Copy } from "lucide-react";

import { Bandera } from "@/components/Bandera";
import { StatusBadge } from "@/components/DetailLayout";

export interface PaisRef {
  codigo: string | null;
  nombre: string;
}

/**
 * País en modo compacto para las pantallas de consulta: bandera + clave ISO
 * alpha-3. El nombre completo va en el tooltip (atributo `title`) y en texto
 * accesible, para que la lista no sature la pantalla cuando hay muchos países.
 *
 * Sin clave —snapshots de texto cuyo nombre ya no está en el catálogo— se
 * muestra el nombre completo: el país nunca se pierde de vista.
 *
 * **El nombre accesible lleva `select-none` a propósito.** Sin eso, seleccionar
 * las insignias y copiar producía «DEUAlemania»: el texto para lector de
 * pantalla está clipado, no oculto, y el navegador se lo lleva igual. Con
 * `select-none` sigue en el árbol de accesibilidad —los lectores no usan la
 * selección— pero deja de ensuciar el portapapeles.
 */
export function PaisBadge({ codigo, nombre }: PaisRef) {
  if (!codigo) {
    return <StatusBadge tone="neutral">{nombre}</StatusBadge>;
  }
  return (
    <StatusBadge tone="neutral" title={nombre}>
      <Bandera codigo={codigo} /> <span className="font-mono">{codigo}</span>
      <span className="sr-only select-none"> {nombre}</span>
    </StatusBadge>
  );
}

/**
 * Representación documental de un conjunto de países: los códigos ISO alpha-3
 * separados por coma y espacio, que es el formato que se pega en un correo o en
 * un oficio.
 *
 * Un país sin clave se copia con su nombre. Omitirlo dejaría el portapapeles
 * incompleto sin que se note, y perder un país de origen es peor que que la
 * lista desentone; además así se ve que hay algo que corregir en el catálogo.
 */
export function codigosDePaises(paises: PaisRef[]): string {
  return paises.map((p) => p.codigo ?? p.nombre).join(", ");
}

/**
 * Lista de países en insignias compactas, con salto de línea automático y una
 * acción para copiar los códigos.
 *
 * El botón es la vía fiable, pero **la selección manual también funciona**: el
 * separador invisible que va entre insignias sí es seleccionable, así que un
 * Ctrl+C sobre el bloque devuelve «DEU, BEL, CAN» en vez de pegarlo todo junto.
 * De paso, el lector de pantalla hace la pausa que antes no hacía.
 */
export function PaisesBadges({
  paises,
  etiquetaCopia = "Copiar códigos",
}: {
  paises: PaisRef[];
  /** Texto del botón; se ajusta cuando conviene decir de qué conjunto se trata. */
  etiquetaCopia?: string;
}) {
  const [estado, setEstado] = useState<"listo" | "copiado" | "error">("listo");

  if (paises.length === 0) return null;

  const copiar = async () => {
    try {
      await navigator.clipboard.writeText(codigosDePaises(paises));
      setEstado("copiado");
    } catch {
      // Sin permiso de portapapeles o fuera de contexto seguro. No se pierde
      // nada: la selección manual del bloque devuelve lo mismo.
      setEstado("error");
    }
    setTimeout(() => setEstado("listo"), 2500);
  };

  return (
    <div className="space-y-1">
      <div className="flex flex-wrap items-center gap-1">
        {paises.map((p, i) => (
          <span key={`${p.codigo ?? p.nombre}-${i}`} className="inline-flex items-center">
            <PaisBadge codigo={p.codigo} nombre={p.nombre} />
            {i < paises.length - 1 && <span className="sr-only">, </span>}
          </span>
        ))}
        <button
          type="button"
          onClick={copiar}
          title={`Copiar los ${paises.length} códigos separados por coma`}
          className="ml-1 inline-flex cursor-pointer items-center gap-1 rounded border px-1.5 py-0.5 text-[11px] text-fg-muted transition select-none hover:bg-surface-2 hover:text-fg"
        >
          {estado === "copiado" ? (
            <>
              <Check className="h-3 w-3 text-estado-ok" /> Copiado
            </>
          ) : estado === "error" ? (
            <>
              <Copy className="h-3 w-3" /> Selecciona y copia
            </>
          ) : (
            <>
              <Copy className="h-3 w-3" /> {etiquetaCopia}
            </>
          )}
        </button>
      </div>
    </div>
  );
}
